/**
 * Admin V3 — usePermissions hook (Phase X2-B G3 — backend-driven)
 *
 * Reads `useAuth().user.permissions` (an array of permission strings exposed
 * by `/auth/me` from Phase X2-B-S2) and exposes a `can(permission)` predicate
 * for ergonomic gating of financial-mutation affordances.
 *
 * GOVERNANCE (X2-B G1 r2 C-r2-1):
 *   - Backend `@rbac_permission(...)` remains the authoritative gate. This
 *     hook is purely an ergonomic mirror that decides whether the frontend
 *     SHOWS an affordance the backend would accept.
 *   - HIDE posture: `can(...)` returns FALSE whenever the permission state
 *     cannot be confirmed (no `permissions` field, non-array value, null,
 *     etc.). Operators whose permission state is unresolved see the
 *     read-only view exactly as before.
 *   - `isResolved` distinguishes "no permission" from "permission unknown"
 *     so callers can render a soft banner asking the operator to re-log
 *     if their token predates X2-B-S2 (legacy tokens lack the field).
 *
 * API:
 *   const { user, can, has, isResolved, loading } = usePermissions();
 *   can('admin:commission_rates:update')  → boolean
 *   has('admin')                          → boolean (legacy role check)
 *   isResolved                            → boolean (permissions field present and array-shaped)
 *   loading                               → boolean (AuthContext /auth/me fetch in flight)
 *
 * Backward compat:
 *   - The Phase-1 stub's surface was `{user, can, has}`. r2 adds `isResolved`
 *     but preserves all three existing fields. Existing consumers (currently
 *     none in V3 — verified at G3 time via grep) get tighter semantics on
 *     `can()`: the stub returned `true` for any logged-in user; the
 *     hardened impl returns `true` only when the permission is actually
 *     in the user's resolved permissions set.
 */

import { useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';

const usePermissions = () => {
  const auth = useAuth();
  const user = auth?.user || null;
  // True while the AuthContext `/auth/me` fetch is in flight. Distinct from
  // `isResolved`: `loading` answers "is the auth state still being fetched?",
  // whereas `isResolved` answers "did it resolve WITH a permissions array?".
  // F2 button-flicker guard uses `loading` (not `isResolved`) so a legacy /
  // permission-less session resolves to a stable read-only view instead of a
  // permanent spinner.
  const loading = Boolean(auth?.loading);

  // Build a Set from user.permissions for O(1) lookup. Returns `null`
  // (sentinel for "unknown / unresolved") when the field is absent or not
  // an array. This three-valued state (Set / null / unknown-other) lets
  // `can()` fail-closed without falsely showing "no permission" to a user
  // whose data simply hasn't loaded yet.
  const userPerms = useMemo(() => {
    const list = user?.permissions;
    if (Array.isArray(list)) {
      return new Set(list);
    }
    return null;
  }, [user]);

  const isResolved = userPerms !== null;

  return {
    user,
    loading,
    isResolved,
    can: (permission) => {
      // HIDE-RBAC: when permissions cannot be confirmed, `can` returns false.
      if (!isResolved) return false;
      return userPerms.has(permission);
    },
    // Legacy role check — kept for stub-era callers. Conservative: returns
    // true only on exact role string match. Not used by X2-B G3 (which
    // gates strictly via `can()`).
    has: (role) => Boolean(user?.role === role),
  };
};

export default usePermissions;
